import { EonUiTagPill } from "@eon-ui/eon-ui-components-react";
import { ThemedIcon } from "@me8eon/icons";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import useAidwopsStore from "../AidwopsStore";
import type { AskContext } from "../aidwops.types";
import SearchInputBox from "./SearchInputBox";
import { AIDWOPS_ASSISTANT_PROMPTS } from "./ExpertDashboard.constants";

function toDisplayFirstName(name?: string | null) {
    const rawFirstName = name?.includes(",")
        ? name.split(",")[1]?.trim()
        : name?.trim().split(/\s+/)[0];

    if (!rawFirstName) {
        return "there";
    }

    return (
        rawFirstName.charAt(0).toUpperCase() +
        rawFirstName.slice(1).toLowerCase()
    );
}

export function SearchLanding({
    onQueueBannerClick,
    onSubmit,
    context,
}: {
    onQueueBannerClick: () => void;
    onSubmit?: () => void;
    context?: AskContext;
}) {
    const { name } = useUserData();
    const jwtToken = useJwtToken();
    const value = useAidwopsStore((state) => state.draft);
    const setValue = useAidwopsStore((state) => state.setDraft);
    const queue = useAidwopsStore((state) => state.queue);
    const submitPrompt = useAidwopsStore((state) => state.submitPrompt);
    const resetChat = useAidwopsStore((state) => state.resetChat);
    const displayName = toDisplayFirstName(name);
    const submitFreshPrompt = (prompt: string) => {
        const trimmedPrompt = prompt.trim();
        if (!trimmedPrompt) {
            return;
        }

        resetChat();
        onSubmit?.();
        void submitPrompt(trimmedPrompt, getAccessToken, context);
    };

    const getAccessToken = async () => {
        const accessToken = await jwtToken();
        if (!accessToken) {
            throw new Error("Unable to acquire access token for expert API");
        }
        return accessToken;
    };

    const submit = () => {
        submitFreshPrompt(value);
    };

    return (
        <section className="hero">
            <ThemedIcon name="AiIcon64" />
            <span className="hero-title">
                Hey {displayName}, what would you like to decide today?
            </span>
            <div className="hero-help">
                <p className="hero-help-text">
                    I can investigate access issues, explain security events, analyze device compliance, and prepare actions for approval.
                </p>
            </div>
            {queue?.summary?.banner && (
                <div className="chips">
                    <EonUiTagPill
                        icon="alert"
                        text={queue.summary.banner}
                        scheme="red500"
                        isSelected
                        onClick={onQueueBannerClick}
                    />
                </div>
            )}
            <div className="chips">
                {AIDWOPS_ASSISTANT_PROMPTS.map((prompt) => (
                    <button
                        key={prompt}
                        className="chip"
                        onClick={() => submitFreshPrompt(prompt)}
                    >
                        {prompt}
                    </button>
                ))}
            </div>
            <SearchInputBox value={value} setValue={setValue} submit={submit} />
        </section>
    );
}
